import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Create or reuse a connected Stripe account and return either an onboarding
 * link URL (Express — production/live) or a null URL with `ready: true`
 * (Custom — preview/test-mode fast-path that pre-fills GB test-magic values
 * so charges_enabled/payouts_enabled flip on immediately, no hosted flow).
 *
 * The two code paths are gated by `isStripeTestMode()` so switching back to
 * Express for live is purely a matter of the STRIPE_TEST_MODE flag flipping
 * off — no app changes required.
 */
export const createConnectOnboardingLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      origin: z.string().url(),
      returnPath: z.string().startsWith("/").max(200).optional(),
    }).parse(d),
  )

  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { getStripe } = await import("./stripe-checkout.server");
    const { isStripeTestMode } = await import("./stripe-mode.server");
    const stripe = getStripe();
    const testMode = isStripeTestMode();

    const { data: profile, error: profErr } = await supabase
      .from("profiles")
      .select("stripe_account_id")
      .eq("id", userId)
      .maybeSingle();
    if (profErr) throw new Error(profErr.message);

    let accountId = profile?.stripe_account_id ?? null;

    if (!accountId) {
      const { data: userRes } = await supabase.auth.getUser();
      const email = userRes.user?.email;

      if (testMode) {
        // Preview / test-mode fast-path: fully-provisioned GB Custom account.
        // Uses Stripe test-magic identity + address + bank values so the
        // account is instantly charges/payouts-enabled with no hosted flow.
        // https://docs.stripe.com/connect/testing
        const nowSec = Math.floor(Date.now() / 1000);
        const account = await stripe.accounts.create({
          type: "custom",
          country: "GB",
          email,
          capabilities: {
            card_payments: { requested: true },
            transfers: { requested: true },
          },
          business_type: "individual",
          business_profile: {
            mcc: "5945", // Hobby, Toy, and Game Shops
            url: "https://bidnovaauctions.test",
            product_description: "Trading card auctions (test)",
          },
          individual: {
            first_name: "Jenny",
            last_name: "Rosen",
            email,
            dob: { day: 1, month: 1, year: 1901 },
            address: {
              line1: "address_full_match",
              city: "London",
              postal_code: "WC2N 5DU",
              country: "GB",
            },
            phone: "+441632960000",
          },
          external_account: {
            object: "bank_account",
            country: "GB",
            currency: "gbp",
            routing_number: "108800",
            account_number: "00012345",
            account_holder_name: "Jenny Rosen",
            account_holder_type: "individual",
          } as unknown as string,
          tos_acceptance: { date: nowSec, ip: "127.0.0.1" },
          metadata: { user_id: userId, is_test: "true", connect_type: "custom_test" },
        });
        accountId = account.id;

        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { error: upErr } = await supabaseAdmin
          .from("profiles")
          .update({
            stripe_account_id: accountId,
            stripe_charges_enabled: !!account.charges_enabled,
            stripe_payouts_enabled: !!account.payouts_enabled,
            stripe_details_submitted: !!account.details_submitted,
          })
          .eq("id", userId);
        if (upErr) throw new Error(upErr.message);

        return {
          url: null,
          ready: !!(account.charges_enabled && account.payouts_enabled),
          mode: "custom_test" as const,
        };
      }

      // Live / production path — Express + hosted onboarding.
      const account = await stripe.accounts.create({
        type: "express",
        country: "GB",
        email,
        capabilities: {
          card_payments: { requested: true },
          transfers: { requested: true },
        },
        business_type: "individual",
        metadata: { user_id: userId },
      });
      accountId = account.id;

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { error: upErr } = await supabaseAdmin
        .from("profiles")
        .update({ stripe_account_id: accountId })
        .eq("id", userId);
      if (upErr) throw new Error(upErr.message);
    }

    // Existing account (either mode): if in test mode and the account is
    // already Custom, just resync status and return — Custom has no hosted flow.
    const existing = await stripe.accounts.retrieve(accountId);
    if (existing.type === "custom") {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin
        .from("profiles")
        .update({
          stripe_charges_enabled: !!existing.charges_enabled,
          stripe_payouts_enabled: !!existing.payouts_enabled,
          stripe_details_submitted: !!existing.details_submitted,
        })
        .eq("id", userId);
      return {
        url: null,
        ready: !!(existing.charges_enabled && existing.payouts_enabled),
        mode: "custom_test" as const,
      };
    }

    const link = await stripe.accountLinks.create({
      account: accountId,
      refresh_url: `${data.origin}${data.returnPath ?? "/dashboard"}?stripe=refresh`,
      return_url: `${data.origin}${data.returnPath ?? "/dashboard"}?stripe=return`,
      type: "account_onboarding",
    });

    return { url: link.url, ready: false, mode: "express" as const };
  });

/** Pull latest account status from Stripe and persist onboarding flags. */
export const refreshConnectStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("stripe_account_id,stripe_charges_enabled,stripe_payouts_enabled,stripe_details_submitted")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!profile?.stripe_account_id) {
      return { hasAccount: false, chargesEnabled: false, payoutsEnabled: false, detailsSubmitted: false };
    }

    const { getStripe } = await import("./stripe-checkout.server");
    const stripe = getStripe();
    const acct = await stripe.accounts.retrieve(profile.stripe_account_id);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("profiles")
      .update({
        stripe_charges_enabled: !!acct.charges_enabled,
        stripe_payouts_enabled: !!acct.payouts_enabled,
        stripe_details_submitted: !!acct.details_submitted,
      })
      .eq("id", userId);

    return {
      hasAccount: true,
      chargesEnabled: !!acct.charges_enabled,
      payoutsEnabled: !!acct.payouts_enabled,
      detailsSubmitted: !!acct.details_submitted,
    };
  });

/** Admin-only: release payout to seller via Stripe transfer, then mark released. */
export const releaseOrderPayoutViaStripe = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orderId: z.string().uuid(),
      reference: z.string().max(200).optional().nullable(),
      notes: z.string().max(1000).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: isAdmin, error: roleErr } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (roleErr) throw new Error(roleErr.message);
    if (!isAdmin) throw new Error("Forbidden");

    const { performOrderPayout } = await import("./order-payout.server");
    const result = await performOrderPayout(data.orderId, {
      source: "admin",
      actorId: userId,
      reference: data.reference ?? null,
      notes: data.notes ?? null,
    });
    if (result.blocked) {
      return { blocked: true as const, reason: result.reason, message: result.message };
    }
    return { transferId: result.transferId, amount: result.amount, alreadyReleased: result.alreadyReleased };
  });



/** Admin-only: refund the buyer via Stripe. Reverses any prior transfer first, then refunds.
 *  Supports partial refunds. All amounts derived server-side. Uses stable idempotency
 *  keys so repeated admin clicks cannot double-refund or double-reverse. */
export const refundOrderViaStripe = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orderId: z.string().uuid(),
      disputeId: z.string().uuid().optional().nullable(),
      refundAmountPence: z.number().int().positive().optional().nullable(),
      notes: z.string().max(1000).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: isAdmin, error: roleErr } = await supabase.rpc("has_role", {
      _user_id: userId, _role: "admin",
    });
    if (roleErr) throw new Error(roleErr.message);
    if (!isAdmin) throw new Error("Forbidden");

    const { data: order, error: ordErr } = await supabase
      .from("orders")
      .select("id,status,amount_pence,stripe_payment_intent_id,stripe_transfer_id")
      .eq("id", data.orderId)
      .maybeSingle();
    if (ordErr) throw new Error(ordErr.message);
    if (!order) throw new Error("Order not found");
    if (order.status === "refunded") throw new Error("Order already refunded");
    if (!order.stripe_payment_intent_id) throw new Error("No payment intent recorded for this order");

    const refundAmount = data.refundAmountPence ?? order.amount_pence;
    if (refundAmount <= 0 || refundAmount > order.amount_pence) {
      throw new Error("Invalid refund amount");
    }

    const { getStripe } = await import("./stripe-checkout.server");
    const stripe = getStripe();

    // Reverse the seller transfer first, if it exists. Uses a stable idempotency
    // key so retries do not stack reversals. Skip if already fully reversed.
    if (order.stripe_transfer_id) {
      try {
        const transfer = await stripe.transfers.retrieve(order.stripe_transfer_id);
        const alreadyReversed = (transfer.amount_reversed ?? 0) >= (transfer.amount ?? 0);
        if (!alreadyReversed) {
          const reversalIdemKey = `dispute_reversal_${order.id}_${order.stripe_transfer_id}`;
          await stripe.transfers.createReversal(order.stripe_transfer_id, {
            refund_application_fee: false,
            metadata: { order_id: order.id, reversed_by: userId, dispute_id: data.disputeId ?? "" },
          }, { idempotencyKey: reversalIdemKey });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[dispute-refund] transfer reversal failed", { orderId: order.id, error: msg });
        throw new Error("Transfer reversal failed — refund not attempted");
      }
    }

    // Refund. Stable idempotency key includes dispute + amount so partial
    // refunds vs full-refund retries are distinct keys.
    const refundIdemKey = `dispute_refund_${order.id}_${data.disputeId ?? "none"}_${refundAmount}`;
    let refund;
    try {
      refund = await stripe.refunds.create(
        {
          payment_intent: order.stripe_payment_intent_id,
          amount: refundAmount,
          metadata: {
            order_id: order.id,
            refunded_by: userId,
            dispute_id: data.disputeId ?? "",
          },
        },
        { idempotencyKey: refundIdemKey },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[dispute-refund] refund failed", { orderId: order.id, error: msg });
      throw new Error("Refund failed — please retry from the Dispute Centre");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    if (data.disputeId) {
      // Finalize the dispute via the new admin_finalize_dispute_buyer function.
      const { error: dErr } = await supabaseAdmin.rpc("admin_finalize_dispute_buyer" as never, {
        _dispute_id: data.disputeId,
        _refund_amount_pence: refundAmount,
        _notes: data.notes || `Stripe refund ${refund.id}`,
      } as never);
      if (dErr) throw new Error(dErr.message);
    } else {
      await supabaseAdmin.from("orders").update({ status: "refunded" }).eq("id", order.id);
    }

    await supabaseAdmin.rpc("_log_payment_event" as never, {
      _order_id: order.id,
      _event_type: "refund_issued",
      _actor_id: userId,
      _amount_pence: refundAmount,
      _metadata: {
        refund_id: refund.id,
        reversed_transfer_id: order.stripe_transfer_id,
        source: data.disputeId ? "dispute_resolved_buyer" : "admin_refund",
        partial: refundAmount < order.amount_pence,
      },
    } as never);

    return { refundId: refund.id, amount: refundAmount, reversed: !!order.stripe_transfer_id };
  });



/** Admin-only: return the connected Stripe platform account id. */
export const getStripePlatformAccount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin, error } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (error) throw new Error(error.message);
    if (!isAdmin) throw new Error("Forbidden");
    const { getStripe } = await import("./stripe-checkout.server");
    const stripe = getStripe();
    const acct = await stripe.accounts.retrieve(undefined as unknown as string);
    return {
      id: acct.id,
      businessName: acct.business_profile?.name ?? null,
      country: acct.country ?? null,
      chargesEnabled: !!acct.charges_enabled,
      payoutsEnabled: !!acct.payouts_enabled,
    };
  });
